import { IconProps, Loading } from '@drift-labs/icons';
import { ButtonHTMLAttributes, ComponentType } from 'react';
import { cj, cm } from 'src/utils/classNames';
import { WithClassName, WithRef } from 'src/utils/UtilityTypes';

interface BaseProps
	extends WithClassName, ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: 'primary' | 'secondary' | 'tertiary';
	size: 'sm' | 'md' | 'lg' | 'xl';
	/**
	 * Only optional if you pass `children` (which you should only do as a last resort).
	 */
	icon?: ComponentType<IconProps>;
	/**
	 * If `true`, replaces the icon with a spinner and sets `disabled` to `true`.
	 */
	isLoading?: boolean;
	/**
	 * An escape hatch for when you *really* need to render your own child node.
	 * In general, you should 1) use `icon`, 2) use `className` to override styles,
	 * and / or 3) check with Design to ensure this anomaly is absolutely necessary.
	 */
	children?: React.ReactNode;
	dataTestId?: string;
}

interface WithIconProps extends BaseProps {
	icon: ComponentType<IconProps>;
	children?: never;
}

interface WithChildrenProps extends BaseProps {
	children: React.ReactNode;
	icon?: never;
	isLoading?: never;
}

export type IconButtonProps = WithRef<
	WithChildrenProps | WithIconProps,
	HTMLButtonElement
>;

export default function IconButton({
	variant = 'primary',
	size,
	className,
	icon: Icon,
	isLoading,
	disabled,
	children,
	dataTestId,
	...rest
}: IconButtonProps) {
	const variantClassName = {
		primary: cj(
			'bg-input-bg text-text-default hover:bg-button-disabled disabled:bg-button-disabled disabled:text-text-disabled',
			// We use `outline` to maintain consistent height with other "non-bordered" buttons.
			'border-none outline -outline-offset-1 outline-button-secondary-bg outline-solid'
		),
		secondary: cj(
			'bg-button-secondary-bg text-text-default hover:bg-button-secondary-bg-hover',
			'disabled:bg-button-disabled disabled:text-text-disabled'
		),
		tertiary:
			'text-text-secondary hover:text-text-default disabled:text-text-disabled',
	}[variant];

	const sizeClassName = {
		xl: 'p-md rounded-sm',
		lg: 'p-md rounded-sm',
		md: 'p-sm rounded-sm',
		sm: 'p-xs rounded-xs',
	}[size];

	const iconSize = {
		xl: 28,
		lg: 20,
		md: 18,
		sm: 16,
	}[size];

	return (
		<button
			className={cm(
				'grid place-items-center',
				variantClassName,
				// `tertiary` variant doesn't have padding / border.
				variant !== 'tertiary' && sizeClassName,
				className
			)}
			disabled={isLoading || disabled}
			data-testid={dataTestId}
			{...rest}
		>
			{Icon && !isLoading && <Icon size={iconSize} />}
			{isLoading && <Loading className="animate-spin" size={iconSize} />}
			{children}
		</button>
	);
}
